import {FC} from 'react'
import {Modal} from 'react-bootstrap'
import Button from './Button'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  confirmBtnClickHandler: () => void
  toDeleteItemTitle?: string
  isLoading?: boolean
  title?: string
  message?: string
  confirmButtonText?: string
  type?: 'delete' | 'activate' | 'deactivate' | 'default'
}

const ConfirmationModal: FC<Props> = ({
  isModalOpen,
  hideModal,
  confirmBtnClickHandler,
  toDeleteItemTitle,
  isLoading,
  title = 'Are you absolutely sure?',
  message,
  confirmButtonText = 'Delete',
  type = 'delete',
}) => {
  const getButtonStyle = () => {
    switch (type) {
      case 'delete':
        return {
          backgroundColor: '#C10007',
          color: 'white',
          hoverColor: '#D6302A',
        }
      case 'activate':
        return {
          backgroundColor: '#0E9F6E',
          color: 'white',
          hoverColor: '#148363',
        }
      case 'deactivate':
        return {
          backgroundColor: '#C10007',
          color: 'white',
          hoverColor: '#D6302A',
        }
      default:
        return {
          backgroundColor: '#0e9f6e',
          color: 'white',
          hoverColor: '#148363',
        }
    }
  }

  const buttonStyle = getButtonStyle()

  const getLoadingText = () => {
    if (confirmButtonText === 'Remove') {
      return 'Removing'
    }

    if (confirmButtonText === 'Update') {
      return 'Updating'
    }

    switch (type) {
      case 'delete':
        return 'Deleting'
      case 'activate':
        return 'Activating'
      case 'deactivate':
        return 'Deactivating'
      default:
        return 'Processing'
    }
  }

  return (
    <Modal
      show={isModalOpen}
      onHide={hideModal}
      backdropClassName='bg-opacity-1'
      contentClassName='modal-rounded-sm'
      centered
    >
      <Modal.Body style={{borderRadius: '16px', padding: '24px'}}>
        <div className='d-flex flex-column' style={{gap: '10px'}}>
          <h3
            className='text-dark fw-medium'
            style={{
              fontSize: '18px',
            }}
          >
            {title}
          </h3>

          <p className='text-secondary-gray mb-0' style={{fontSize: '13px'}}>
            {message || (
              <>
                This action cannot be undone. This will permanently{' '}
                {type === 'delete' ? 'delete' : type}{' '}
                <span className='fw-bold'>{toDeleteItemTitle ?? 'selected item'}</span>{' '}
                {type === 'delete' ? ' and remove it from our servers' : ''}.
              </>
            )}
          </p>

          <div className='d-flex justify-content-end mt-4' style={{gap: '12px'}}>
            <Button data-bs-dismiss='modal' onClick={hideModal} disabled={isLoading}>
              Cancel
            </Button>

            <button
              className='btn btn-sm fs-6 d-flex align-items-center justify-content-center'
              data-bs-dismiss='modal'
              onClick={confirmBtnClickHandler}
              style={{
                backgroundColor: buttonStyle.backgroundColor,
                color: buttonStyle.color,
                border: '1px solid transparent',
                height: '40px',
                opacity: `${isLoading ? '0.5' : '1'}`,
              }}
              disabled={isLoading}
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = buttonStyle.hoverColor)
              }
              onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor)
              }
            >
              {isLoading ? (
                <>
                  {`${getLoadingText()}...`}
                  <span className='spinner-border spinner-border-sm align-middle m-2'></span>
                </>
              ) : (
                confirmButtonText ||
                (type === 'delete'
                  ? 'Delete'
                  : type === 'activate'
                  ? 'Activate'
                  : type === 'deactivate'
                  ? 'Deactivate'
                  : 'Confirm')
              )}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default ConfirmationModal
